/**
 * Префикс приложения (basePath) — платформа может жить в подпапке домена,
 * например https://alphastrategy.ru/big5, рядом с другими сайтами.
 *
 * ИНВАРИАНТ. В коде, в БД и в API все адреса — «пути приложения» без
 * префикса: '/api/…', '/uploads/…', '/desk'. Так значения в БД (avatarUrl,
 * attachment.url, notification.href) переносимы между установками, а
 * локальная разработка без префикса ведёт себя как раньше. Префикс
 * появляется только на границе с браузером («сток»): атрибут href/src в DOM,
 * window.location/open, XHR, sendBeacon, путь socket.io, Location и
 * абсолютные ссылки в ответах сервера, Path у cookie.
 *
 * Что Next префиксует сам — Link, router.push/replace, redirect() в
 * страницах и layout, redirects/rewrites/headers и matcher proxy из
 * next.config, public/ и /_next — оборачивать НЕЛЬЗЯ: выйдет /big5/big5/….
 * От двойного префикса страхует только идемпотентность withBase, но
 * Next-у она не передаётся.
 *
 * fetch('/api/…') отдельно не оборачиваем: в <head> корневого layout стоит
 * прокладка (fetchBasePathShimScript ниже), она префиксует запросы до
 * загрузки любого чанка.
 *
 * Источник один — NEXT_BASE_PATH ('' или '/seg') при сборке и запуске.
 * next.config.ts кладёт его в NEXT_PUBLIC_BASE_PATH, и Next вшивает его в
 * клиентский и серверный бандлы. server.ts Next не бандлит, поэтому здесь
 * же читаем и исходную переменную.
 *
 * Без 'use client' и без импортов: модуль нужен и server.ts (tsc), и
 * proxy, и клиенту.
 */

/** '' или '/seg' (можно '/a/b'); конечный '/' срезается, всё прочее — ошибка. */
export function normalizeBasePath(raw: string | undefined | null): string {
  const v = (raw ?? '').trim();
  if (v === '' || v === '/') return '';
  const s = v.replace(/\/+$/, '');
  // Только безопасные сегменты: префикс попадает в регулярки matcher-а,
  // в Path cookie и в inline-скрипт — экранировать там нечего не должно быть.
  if (!/^(\/[A-Za-z0-9._~-]+)+$/.test(s) || /\/\.{1,2}(\/|$)/.test(s)) {
    throw new Error(`NEXT_BASE_PATH='${raw}' — ожидается пусто или путь вида '/big5'`);
  }
  return s;
}

export const BASE_PATH: string = normalizeBasePath(
  process.env.NEXT_PUBLIC_BASE_PATH || process.env.NEXT_BASE_PATH,
);

/** Путь своего приложения: одиночный '/' в начале (не '//host' и не '/\\host'). */
function isAppAbsolute(p: string): boolean {
  return p.charCodeAt(0) === 47 /* / */ && p[1] !== '/' && p[1] !== '\\';
}

/** Уже под префиксом: сам префикс или он с продолжением после границы. */
function hasBase(base: string, p: string): boolean {
  if (!p.startsWith(base)) return false;
  const next = p[base.length];
  return next === undefined || next === '/' || next === '?' || next === '#';
}

/**
 * Путь приложения → адрес для браузера. Идемпотентна; чужие адреса
 * ('https://…', '//host', 'blob:', 'data:', относительные 'x/y') не трогает.
 */
export function withBaseFor(base: string, p: string): string {
  if (!base || typeof p !== 'string' || !isAppAbsolute(p) || hasBase(base, p)) return p;
  // '/', '/?x', '/#y' → '/big5', '/big5?x' — без слэша, иначе Next отвечает 308.
  if (p === '/' || p[1] === '?' || p[1] === '#') return base + p.slice(1);
  return base + p;
}

/** Адрес из браузера (location.pathname, Request.url) → путь приложения. */
export function stripBaseFor(base: string, pathname: string): string {
  if (!base || !hasBase(base, pathname)) return pathname;
  const rest = pathname.slice(base.length);
  return rest === '' || rest[0] !== '/' ? '/' + rest : rest;
}

export function withBase(p: string): string {
  return withBaseFor(BASE_PATH, p);
}

export function stripBase(pathname: string): string {
  return stripBaseFor(BASE_PATH, pathname);
}

/** Текущий путь приложения с запросом — для ?next= и сравнения с маршрутами. */
export function appPathNow(): string {
  if (typeof window === 'undefined') return '/';
  return stripBase(window.location.pathname) + window.location.search;
}

/** Жёсткая навигация (полная перезагрузка) на путь приложения. */
export function goTo(path: string, opts: { replace?: boolean } = {}): void {
  const url = withBase(path);
  if (opts.replace) window.location.replace(url);
  else window.location.assign(url);
}

/** Абсолютная ссылка наружу (письма, QR, копировать ссылку): origin + префикс + путь. */
export function absoluteAppUrl(origin: string, path: string): string {
  return origin.replace(/\/+$/, '') + withBase(path.startsWith('/') ? path : '/' + path);
}

/**
 * Path для cookie сессии: под подпапкой — только своя подпапка, иначе
 * alpha_session уходила бы всем сайтам домена (и их выход стирал бы нашу).
 */
export const SESSION_COOKIE_PATH: string = BASE_PATH || '/';

/**
 * Inline-скрипт для <head> корневого layout: префиксует fetch к своему
 * origin. Почему не useEffect провайдера: эффекты детей выполняются раньше
 * эффектов родителя, и первые fetch страниц при монтировании ушли бы без
 * префикса; модуль с побочным эффектом тоже ненадёжен — порядок загрузки
 * чанков не гарантирован. Inline-скрипт исполняется раньше любого кода
 * приложения: async-чанки Next стоят в <head> выше и могут загрузиться
 * первыми, но модули приложения вычисляются только по данным RSC
 * (self.__next_f в <body>), а те разбираются уже после <head>.
 *
 * Правило держится на том, что приложение не ходит на свой origin вне
 * префикса: строка на одиночный '/' и URL того же origin вне префикса
 * получают префикс; уже префиксованное (RSC-запросы и server actions Next),
 * чужой origin, относительные пути и Request-объекты не трогаются.
 * Логика повторяет withBaseFor — тест в scripts/test-audit-core.ts гоняет
 * оба на одних входах.
 */
export function fetchBasePathShimScript(base: string): string {
  const b = JSON.stringify(normalizeBasePath(base));
  return `(function(){var B=${b};if(!B||!window.fetch||window.fetch.__basePath)return;` +
    `function w(p){if(p.charAt(0)!=='/'||p.charAt(1)==='/'||p.charAt(1)==='\\\\')return p;` +
    `if(p.indexOf(B)===0){var c=p.charAt(B.length);if(c===''||c==='/'||c==='?'||c==='#')return p;}` +
    `if(p==='/'||p.charAt(1)==='?'||p.charAt(1)==='#')return B+p.slice(1);return B+p;}` +
    `var f=window.fetch;var g=function(i,o){` +
    `if(typeof i==='string')i=w(i);` +
    `else if(typeof URL!=='undefined'&&i instanceof URL&&i.origin===location.origin){var q=w(i.pathname);if(q!==i.pathname){i=new URL(i.href);i.pathname=q;}}` +
    `return f.call(window,i,o);};g.__basePath=true;window.fetch=g;})();`;
}
